import { useRef, useCallback, useEffect } from 'react';

// Friccion de la inercia por cuadro: cuanto se conserva de la velocidad.
const FRICCION = 0.92;

/**
 * Mano para recorrer el Gantt: arrastrar el fondo mueve la linea de tiempo
 * hacia adelante y atras (y arriba/abajo). Si se suelta con impulso sigue un
 * poco y frena, como cuando se empuja un papel sobre la mesa.
 *
 * Las barras, botones y links tienen su propio arrastre o click, asi que no
 * arrancan la mano. Si hubo arrastre, el click de soltar se descarta (para no
 * fijar una fecha en una fila "Sin fecha", por ejemplo).
 */
export const usePaneo = (scrollRef) => {
  const inercia = useRef(null);

  const frenar = useCallback(() => {
    if (inercia.current) cancelAnimationFrame(inercia.current);
    inercia.current = null;
  }, []);

  useEffect(() => frenar, [frenar]);

  const onMouseDown = useCallback((e) => {
    const el = scrollRef.current;
    if (!el || e.button !== 0) return;
    if (e.target.closest('button, a, input, textarea, select, [data-sin-paneo]')) return;
    // Click sobre las barras de scroll del propio contenedor.
    if (e.target === el) {
      const { offsetX, offsetY } = e.nativeEvent;
      if (offsetX >= el.clientWidth || offsetY >= el.clientHeight) return;
    }
    frenar();
    e.preventDefault(); // que no seleccione texto mientras arrastra

    const x0 = e.clientX;
    const y0 = e.clientY;
    const left0 = el.scrollLeft;
    const top0 = el.scrollTop;
    let movido = false;
    let muestras = [{ x: x0, y: y0, t: performance.now() }];
    document.body.style.cursor = 'grabbing';
    el.style.cursor = 'grabbing';

    const mover = (ev) => {
      const dx = ev.clientX - x0;
      const dy = ev.clientY - y0;
      if (!movido && Math.hypot(dx, dy) > 3) movido = true;
      el.scrollLeft = left0 - dx;
      el.scrollTop = top0 - dy;
      const ahora = performance.now();
      muestras.push({ x: ev.clientX, y: ev.clientY, t: ahora });
      muestras = muestras.filter((m) => ahora - m.t < 100);
    };

    const soltar = () => {
      window.removeEventListener('mousemove', mover);
      window.removeEventListener('mouseup', soltar);
      document.body.style.cursor = '';
      el.style.cursor = '';
      if (!movido) return;

      const tragar = (ce) => { ce.stopPropagation(); ce.preventDefault(); };
      window.addEventListener('click', tragar, { capture: true, once: true });
      setTimeout(() => window.removeEventListener('click', tragar, { capture: true }), 0);

      // Impulso: velocidad de los ultimos 100 ms, en px por cuadro.
      const a = muestras[0];
      const b = muestras[muestras.length - 1];
      const dt = b.t - a.t;
      if (dt <= 0) return;
      let vx = ((b.x - a.x) / dt) * 16;
      let vy = ((b.y - a.y) / dt) * 16;
      if (Math.hypot(vx, vy) < 2) return;
      const paso = () => {
        el.scrollLeft -= vx;
        el.scrollTop -= vy;
        vx *= FRICCION;
        vy *= FRICCION;
        inercia.current = Math.hypot(vx, vy) > 0.4 ? requestAnimationFrame(paso) : null;
      };
      inercia.current = requestAnimationFrame(paso);
    };

    window.addEventListener('mousemove', mover);
    window.addEventListener('mouseup', soltar);
  }, [scrollRef, frenar]);

  // La rueda del mouse corta la inercia: manda el usuario.
  return { onMouseDown, onWheel: frenar };
};

export default usePaneo;
